import {useEffect, useState} from 'react';

import {DefaultRootState, useSelector} from 'react-redux';

import type {UseQuery} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type {QueryDefinition} from '@reduxjs/toolkit/query/react';
import type {ListResponseType} from 'sweet-types';

export function createGenericHookReadingFromById<
  UseQueryHookType extends UseQuery<
    QueryDefinition<any, any, any, any, 'sweetAPI'>
  >
>(
  useAPIListHook: UseQueryHookType,
  byIdSelector: (state: DefaultRootState) => {[id: string]: any},
  idGetter: (item: any) => string = (item) => item.id,
  readDataFromByIdDict: (
    data: any,
    byId: {[id: string]: any},
    idGetter: (item: any) => string
  ) => any
): UseQueryHookType {
  // @ts-expect-error bla
  return (...args) => {
    const {data, currentData, isLoading, isFetching, ...response} =
      useAPIListHook(...args);
    const byId = useSelector(byIdSelector);

    const [localIsLoading, setLocalIsLoading] = useState(false);
    const [localIsFetching, setLocalIsFetching] = useState(false);

    // persist local data in state to prevent frequent refreshes in consumers of the hook.
    const [localData, setLocalData] = useState(
      readDataFromByIdDict(data, byId, idGetter)
    );

    const [localCurrentData, setLocalCurrentData] = useState(
      readDataFromByIdDict(currentData, byId, idGetter)
    );

    useEffect(() => {
      setLocalData(readDataFromByIdDict(data, byId, idGetter));
    }, [JSON.stringify(data)]);

    useEffect(() => {
      setLocalCurrentData(readDataFromByIdDict(currentData, byId, idGetter));
    }, [JSON.stringify(currentData)]);

    useEffect(() => {
      if (!isLoading) {
        setLocalData(readDataFromByIdDict(data, byId, idGetter));
        setLocalCurrentData(readDataFromByIdDict(currentData, byId, idGetter));
      }
      setLocalIsLoading(isLoading);
    }, [isLoading]);

    useEffect(() => {
      if (!isFetching) {
        setLocalData(readDataFromByIdDict(data, byId, idGetter));
        setLocalCurrentData(readDataFromByIdDict(currentData, byId, idGetter));
      }
      setLocalIsFetching(isFetching);
    }, [isFetching]);

    return {
      ...response,
      isLoading: localIsLoading,
      isFetching: localIsFetching,
      data: localData,
      currentData: localCurrentData,
    };
  };
}

export function createListHookReadingFromById<
  UseQueryHookType extends UseQuery<
    QueryDefinition<any, any, any, any, 'sweetAPI'>
  >
>(
  useAPIListHook: UseQueryHookType,
  byIdSelector: (state: DefaultRootState) => {[id: string]: any},
  idGetter?: (item: any) => string
): UseQueryHookType {
  return createGenericHookReadingFromById(
    useAPIListHook,
    byIdSelector,
    idGetter,
    <I extends {id: string | number}>(
      data: null | ListResponseType<I>,
      byId: {[id: string]: I},
      idGetter: (item: I) => string
    ): null | ListResponseType<I> =>
      data == null
        ? null
        : {
            ...data,
            results: data.results.map((item) => byId[idGetter(item)]),
          }
  );
}

export function createDetailHookReadingFromById<
  UseQueryHookType extends UseQuery<
    QueryDefinition<any, any, any, any, 'sweetAPI'>
  >
>(
  useAPIListHook: UseQueryHookType,
  byIdSelector: (state: DefaultRootState) => {[id: string]: any},
  idGetter?: (item: any) => string
): UseQueryHookType {
  return createGenericHookReadingFromById(
    useAPIListHook,
    byIdSelector,
    idGetter,
    (data, byId, idGetter) => (data == null ? null : byId[idGetter(data)])
  );
}
